import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom';
import { HiOutlinePencil, HiOutlinePhotograph, HiOutlineUpload, HiOutlineTrash, HiOutlineStar, HiOutlineEmojiSad, HiOutlineX, HiOutlineExclamation, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import { MdNavigateBefore } from 'react-icons/md';
import '../css/admin.css'
import '../css/edit-item.css'
import ItemService from '../service/ItemService';
import ShopService from '../service/ShopService';
import { formatRupee } from '../components/Price';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import ImageCropper from '../components/ImageCropper';
import ItemVideoCard from '../components/ItemVideoCard';
import { PRODUCT_PHOTO, checkPhotoFile, cleanCopy } from '../util/imageFile';


// Mirrors ItemSizeService: finalPrice = mrp - (mrp / 100 * discount), truncated.
const previewFinalPrice = (mrp, discount) => {
    const m = Number(mrp)
    const d = Number(discount) || 0
    if (!Number.isFinite(m) || m <= 0) return null
    return Math.trunc(m - (m / 100) * d)
}

const isWholeNumber = (value) => /^\d+$/.test(String(value).trim())

const EditItem = () => {

    const location = useLocation();
    const history = useHistory();
    const fileRef = useRef(null);
    const itemIdFromRoute = location.state?.id;

    // ---------------- STATE ----------------
    const [item, setItem] = useState(null);
    const [currentImage, setCurrentImage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [gender, setGender] = useState("");
    const [brandId, setBrandId] = useState("");
    const [brands, setBrands] = useState(null);
    const [sizeList, setSizeList] = useState(null);
    const [listItems, setListItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [initialMrp, setInitialMrp] = useState('');
    const [initialDiscount, setInitialDiscount] = useState('0');
    const [addSizesError, setAddSizesError] = useState('');
    const [isAddingSizes, setIsAddingSizes] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedPreview, setSelectedPreview] = useState(null);
    const [cropSource, setCropSource] = useState(null);
    useEffect(() => () => { if (selectedPreview) URL.revokeObjectURL(selectedPreview) }, [selectedPreview]);
    const [uploading, setUploading] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const [confirmDeleteImage, setConfirmDeleteImage] = useState(false);
    const [titleOpen, setTitleOpen] = useState(false);
    const [titleText, setTitleText] = useState('');
    const [titleError, setTitleError] = useState('');
    const [sizeEdit, setSizeEdit] = useState(null);
    const [sizeEditError, setSizeEditError] = useState('');
    const [busy, setBusy] = useState(false);
    const [pageError, setPageError] = useState('');
    const [toast, setToast] = useState('');

    const [savingSale, setSavingSale] = useState(false);

    // ---------------- DATA ----------------
    const loadItem = useCallback((keepImage) => {
        if (!itemIdFromRoute) {
            setIsLoading(false)
            return
        }
        ItemService.getMyitemById(itemIdFromRoute)
            .then((response) => {
                if (response.status === 200) {
                    const data = response.data.data;
                    setItem(data)
                    setTitleText(data.title)
                    setIsEnabled(!!data.isAvailable)
                    setGender(data.itemGender == null ? '' : String(data.itemGender))
                    setBrandId(data.brandId == null ? '' : String(data.brandId))
                    setCurrentImage(prev => (keepImage && prev && [data.imageUrl, data.imageUrl1, data.imageUrl2, data.imageUrl3].includes(prev)) ? prev : data.imageUrl)
                    setLoadError(false)
                }
                setIsLoading(false)
            })
            .catch((err) => {
                console.error(err)
                setLoadError(true)
                setIsLoading(false)
            });
    }, [itemIdFromRoute])

    const refreshItemSizes = useCallback(() => {
        if (!itemIdFromRoute) return
        ItemService.getItemSizes(itemIdFromRoute)
            .then((res) => {
                if (res.status === 200) {
                    setSizeList(res.data.data);
                    setListItems(res.data.data.itemSizeData || []);
                }
            })
            .catch((err) => console.error(err));
    }, [itemIdFromRoute])

    useEffect(() => {
        loadItem(false)
        refreshItemSizes()
    }, [loadItem, refreshItemSizes]);

    useEffect(() => {
        ShopService.getBrands()
            .then((res) => setBrands(Array.isArray(res.data.data) ? res.data.data : []))
            .catch(() => setBrands([]));
    }, []);

    // ---------------- DETAILS ----------------
    const handleChangeGender = (e) => {
        const previous = gender
        const value = e.target.value
        setGender(value);
        const isBoySpecific = value === "1" || value === "3";
        const isGirlSpecific = value === "2" || value === "3";
        ItemService.updateItemGender(item.id, isBoySpecific, isGirlSpecific)
            .then((response) => {
                if (response.status === 200) setToast('Saved')
            })
            .catch(() => {
                setGender(previous)
                setPageError('Could not update who this product is for. Please try again.')
            });
    };

    const handleChangeBrand = (e) => {
        const previous = brandId
        const value = e.target.value
        setBrandId(value);
        ItemService.updateItemBrand(item.id, value)
            .then((response) => {
                if (response.status === 200) setToast(value ? 'Brand saved' : 'Brand removed')
            })
            .catch(() => {
                setBrandId(previous)
                setPageError('Could not update the brand. Please try again.')
            });
    };

    // On sale = shoppers can see and buy it; hidden = only staff can open it
    const toggleEnabled = () => {
        if (savingSale) return
        const next = !isEnabled
        setIsEnabled(next);
        setSavingSale(true)
        setPageError('')
        const formData = new FormData();
        formData.append('isAvailable', next);
        formData.append('itemId', item.id);

        ItemService.updateAvailabilty(formData)
            .then((response) => {
                if (response.status === 200) setToast(next ? 'Product is now on sale' : 'Product hidden from the shop')
            })
            .catch(() => {
                setIsEnabled(!next)
                setPageError('Could not change availability. Please try again.')
            })
            .finally(() => setSavingSale(false));
    };

    const saveTitle = (event) => {
        event.preventDefault()
        const title = titleText.trim()
        if (title.length < 2) {
            setTitleError('Please enter a product name (at least 2 characters).')
            return
        }
        setBusy(true)
        const formData = new FormData();
        formData.append("itemId", item.id);
        formData.append("itemName", title);
        ItemService.updateTitle(formData).then(response => {
            setBusy(false)
            if (response.status === 200) {
                setTitleOpen(false)
                setToast('Name updated')
                loadItem(true)
            }
        }).catch(error => {
            console.log(error)
            setBusy(false)
            setTitleError('Could not update the name. Please try again.')
        });
    }

    // ---------------- PHOTOS ----------------
    const images = item ? [
        { full: item.imageUrl, thumb: item.thump, id: item.imageId, main: true },
        { full: item.imageUrl1, thumb: item.thump1, id: item.image1Id },
        { full: item.imageUrl2, thumb: item.thump2, id: item.image2Id },
        { full: item.imageUrl3, thumb: item.thump3, id: item.image3Id }
    ].filter(img => img.full) : []
    const selected = images.find(img => img.full === currentImage) || images[0]
    const canAddPhoto = item && !item.imageUrl3

    // picking a photo opens the cropper; the cropped 3:4 JPEG waits in the "Upload" row
    const onFileChange = event => {
        const picked = event.target.files[0]
        event.target.value = null
        if (!picked) return
        const problem = checkPhotoFile(picked)
        if (problem) {
            setPhotoError(problem)
        } else {
            setPhotoError('')
            setCropSource(picked)
        }
    };

    const onCropped = (results) => {
        setCropSource(null)
        setSelectedFile(results.photo.file)
        setSelectedPreview(results.photo.url)
    }

    const onCropCancel = (message) => {
        setCropSource(null)
        if (message) setPhotoError(message)
    }

    const clearSelected = () => {
        setSelectedFile(null)
        setSelectedPreview(null)
    }

    const uploaditemImg = () => {
        if (!selectedFile) return;
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('itemId', item.id);

        setUploading(true);
        ItemService.addNewitemImage(formData)
            .then((response) => {
                setUploading(false)
                if (response.status === 200) {
                    clearSelected()
                    setToast('Photo added')
                    loadItem(true)
                }
            })
            .catch(() => {
                setUploading(false)
                setPhotoError('Could not upload the photo. Please try again.')
            });
    };

    const changeMainImage = () => {
        if (!selected || selected.main) return
        const formData = new FormData();
        formData.append("mainImageId", item.imageId);
        formData.append("subImageId", selected.id);

        setUploading(true);
        ItemService.replaceMainImage(formData)
            .then((response) => {
                setUploading(false);
                if (response.status === 200) {
                    setToast('Main photo updated')
                    loadItem(false)
                }
            })
            .catch(() => {
                setUploading(false);
                setPhotoError('Could not change the main photo. Please try again.')
            });
    };

    const deleteImage = () => {
        setBusy(true)
        ItemService.deleteImage(currentImage).then(response => {
            setBusy(false)
            setConfirmDeleteImage(false)
            if (response.status === 200) {
                setToast('Photo deleted')
                loadItem(false)
            }
        }).catch(error => {
            console.log(error)
            setBusy(false)
            setConfirmDeleteImage(false)
            setPhotoError('Could not delete the photo. Please try again.')
        });
    }

    // ---------------- SIZES ----------------
    const addedSizes = new Set((listItems || []).map(s => String(s.size)))

    const handleCheckboxChange = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const updateSizes = (event) => {
        event.preventDefault()
        if (selectedIds.length === 0) {
            setAddSizesError('Pick at least one size to add.')
            return
        }
        if (!isWholeNumber(initialMrp) || Number(initialMrp) <= 0) {
            setAddSizesError('Please enter the MRP as a whole number in rupees.')
            return
        }
        if (!isWholeNumber(initialDiscount) || Number(initialDiscount) > 99) {
            setAddSizesError('Discount must be a whole number between 0 and 99.')
            return
        }
        setAddSizesError('')
        setIsAddingSizes(true)

        const formData = new FormData();
        formData.append("sizeIds", selectedIds);
        formData.append("itemId", item.id);
        formData.append("initialMrp", initialMrp);
        formData.append("initialDiscount", initialDiscount);

        ItemService.updateEmptySizes(formData)
            .then((response) => {
                setIsAddingSizes(false)
                if (response.status === 200) {
                    setToast(`${selectedIds.length} ${selectedIds.length === 1 ? 'size' : 'sizes'} added`)
                    setSelectedIds([]);
                    setInitialMrp('');
                    setInitialDiscount('0');
                    refreshItemSizes()
                }
            })
            .catch(() => {
                setIsAddingSizes(false)
                setAddSizesError('Could not add the sizes. Please try again.')
            });
    };

    const openSizeEditor = (s) => {
        setSizeEditError('')
        setSizeEdit({
            id: s.id,
            size: s.size,
            mrp: s.mrp == null ? '' : String(s.mrp),
            discount: s.discount == null ? '0' : String(s.discount),
            quantity: s.availableQuantity == null ? '0' : String(s.availableQuantity),
            description: s.description || '',
            material: s.material || '',
            chestSize: s.chestSize == null ? '' : String(s.chestSize),
            length: s.length == null ? '' : String(s.length)
        })
    }

    const saveSize = (event) => {
        event.preventDefault()
        const s = sizeEdit
        if (!isWholeNumber(s.mrp) || Number(s.mrp) <= 0) return setSizeEditError('MRP must be a whole number greater than 0.')
        if (!isWholeNumber(s.discount) || Number(s.discount) > 99) return setSizeEditError('Discount must be a whole number between 0 and 99.')
        if (!isWholeNumber(s.quantity)) return setSizeEditError('Stock must be a whole number (0 or more).')
        setSizeEditError('')
        setBusy(true)

        ItemService.updateSingleSizeMap({
            id: s.id,
            mrp: s.mrp,
            discount: s.discount,
            quantity: s.quantity,
            description: s.description,
            material: s.material,
            chestSize: s.chestSize,
            length: s.length
        }).then(response => {
            setBusy(false)
            if (response.status === 200) {
                setSizeEdit(null)
                setToast(`Size ${s.size} updated`)
                refreshItemSizes()
            }
        }).catch(error => {
            console.log(error)
            setBusy(false)
            setSizeEditError('Could not save this size. Please try again.')
        });
    }

    const setSizeField = (key) => (e) => setSizeEdit(prev => ({ ...prev, [key]: e.target.value }))

    // ---------------- RENDER ----------------
    if (!itemIdFromRoute) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlinePhotograph />} title='Open a product to edit it'
                    text='This page needs a product. Choose one from your inventory.'
                    actionLabel='Go to inventory' onAction={() => history.push('/shop/inventory')} />
            </main>
        )
    }

    if (isLoading) {
        return (
            <main className='mv-page' aria-busy='true'>
                <Skeleton width='260px' height={32} />
                <div className='mv-edit-layout' style={{ marginTop: 24 }}>
                    <Skeleton height={420} radius='20px' />
                    <div className='mv-stack-lg'>
                        <Skeleton height={120} radius='14px' />
                        <Skeleton height={220} radius='14px' />
                    </div>
                </div>
            </main>
        )
    }

    if (loadError || !item) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load this product'
                    text='Please check your connection and try again.' actionLabel='Try again'
                    onAction={() => { setIsLoading(true); loadItem(false); refreshItemSizes() }} />
            </main>
        )
    }

    const livePreview = sizeEdit ? previewFinalPrice(sizeEdit.mrp, sizeEdit.discount) : null
    const addPreview = previewFinalPrice(initialMrp, initialDiscount)

    return (
        <main className='mv-page'>
            <button type='button' className='mv-back-link mv-back-btn' onClick={() => history.goBack()}>
                <MdNavigateBefore size={20} /> Back
            </button>

            <div className='mv-page-head mv-edit-head'>
                <div className='mv-edit-title-wrap'>
                    <h1 className='mv-list-title'>{item.title}</h1>
                    <button type='button' className='mv-icon-btn' aria-label='Edit product name'
                        onClick={() => { setTitleText(item.title); setTitleError(''); setTitleOpen(true) }}>
                        <HiOutlinePencil />
                    </button>
                </div>
                <label className={'mv-switch' + (savingSale ? ' is-busy' : '')}>
                    <input type='checkbox' checked={isEnabled} onChange={toggleEnabled} disabled={savingSale}
                        aria-describedby='mv-sale-note' />
                    <span className='mv-switch-track' aria-hidden='true'><span className='mv-switch-thumb' /></span>
                    <span className='mv-switch-label'>{isEnabled ? 'On sale' : 'Hidden from shop'}</span>
                </label>
            </div>

            <div id='mv-sale-note'>
                {!isEnabled &&
                    <div className='mv-alert mv-alert-info mv-edit-alert mv-sale-note'>
                        <HiOutlineEyeOff aria-hidden='true' />
                        <span><strong>Hidden from the shop.</strong> Shoppers can't see or buy this product. Switch it to On sale when it's ready.</span>
                    </div>}
                {isEnabled && listItems && listItems.length === 0 &&
                    <div className='mv-alert mv-alert-info mv-edit-alert mv-sale-note'>
                        <HiOutlineEye aria-hidden='true' />
                        <span>On sale, but add at least one size with a price so shoppers can find and buy it.</span>
                    </div>}
            </div>

            {pageError &&
                <div className='mv-alert mv-alert-error mv-edit-alert' role='alert'>
                    {pageError}
                    <button type='button' className='mv-icon-btn mv-icon-btn-sm' aria-label='Dismiss' onClick={() => setPageError('')}><HiOutlineX /></button>
                </div>}

            <div className='mv-edit-layout'>
                {/* ---------- Photos ---------- */}
                <section className='mv-card mv-edit-photos' aria-label='Photos'>
                    <div className='mv-edit-main'>
                        {selected && <img src={selected.full} alt={item.title} />}
                        {selected && selected.main && <span className='mv-chip mv-chip-accent mv-edit-main-badge'><HiOutlineStar /> Main photo</span>}
                    </div>

                    <div className='mv-edit-thumbs'>
                        {images.map((img, index) =>
                            <button type='button' key={index}
                                className={img.full === currentImage ? 'mv-pdp-thumb current' : 'mv-pdp-thumb'}
                                onClick={() => setCurrentImage(img.full)}
                                aria-label={img.main ? 'Main photo' : `Photo ${index + 1}`} aria-pressed={img.full === currentImage}>
                                <img src={cleanCopy(img.thumb, img.full)} alt='' />
                            </button>
                        )}
                        {canAddPhoto &&
                            <button type='button' className='mv-edit-add-photo' onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading}>
                                <HiOutlinePhotograph /> Add
                            </button>}
                        <input type='file' ref={fileRef} accept='image/jpeg,image/png,image/webp' onChange={onFileChange} hidden />
                    </div>

                    {selectedFile &&
                        <div className='mv-edit-pending'>
                            {selectedPreview && <img className='mv-edit-pending-thumb' src={selectedPreview} alt='Cropped to 3:4, ready to upload' />}
                            <span className='mv-edit-pending-name' title={selectedFile.name}>Cropped to 3:4 · ready to upload</span>
                            <button type='button' className='mv-btn mv-btn-primary mv-btn-sm' onClick={uploaditemImg} disabled={uploading}>
                                <HiOutlineUpload /> {uploading ? 'Uploading…' : 'Upload'}
                            </button>
                            <button type='button' className='mv-icon-btn mv-icon-btn-sm' aria-label='Remove selected file'
                                onClick={clearSelected} disabled={uploading}><HiOutlineX /></button>
                        </div>}

                    {cropSource &&
                        <ImageCropper file={cropSource} steps={[PRODUCT_PHOTO]} title='Crop product photo'
                            onDone={onCropped} onCancel={onCropCancel} />}

                    {selected && !selected.main &&
                        <div className='mv-form-actions'>
                            <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' onClick={changeMainImage} disabled={uploading}>
                                <HiOutlineStar /> Set as main photo
                            </button>
                            <button type='button' className='mv-btn mv-btn-danger mv-btn-sm' onClick={() => setConfirmDeleteImage(true)} disabled={uploading}>
                                <HiOutlineTrash /> Delete photo
                            </button>
                        </div>}

                    {photoError && <div className='mv-alert mv-alert-error' role='alert'>{photoError}</div>}
                    <p className='mv-field-hint'>Up to 4 photos. The main photo is shown in the shop listing.</p>
                </section>

                <div className='mv-stack-lg'>
                    {/* ---------- Video (phones) ---------- */}
                    <ItemVideoCard itemId={item.id} video={item.video} onToast={setToast}
                        onChange={(video) => setItem(prev => prev && { ...prev, video })} />

                    {/* ---------- Details ---------- */}
                    <section className='mv-card mv-edit-details'>
                        <h2 className='mv-card-title'>Details</h2>
                        <div className='mv-field'>
                            <label className='mv-label' htmlFor='item-gender'>Made for</label>
                            <select id='item-gender' className='mv-select' value={gender} onChange={handleChangeGender}>
                                <option value="">Choose…</option>
                                <option value="1">Boys</option>
                                <option value="2">Girls</option>
                                <option value="3">Boys and girls</option>
                            </select>
                        </div>
                        <div className='mv-field'>
                            <div className='mv-label-row'>
                                <label className='mv-label' htmlFor='item-brand'>Brand <span className='mv-label-optional'>(optional)</span></label>
                                <Link to='/shop/inventory?tab=brands' className='mv-label-link'>Manage brands</Link>
                            </div>
                            {brands != null && brands.length === 0 && !brandId ?
                                <p className='mv-field-hint'>No brands yet. Add them in Inventory → Brands, then choose one here.</p> :
                                <select id='item-brand' className='mv-select' value={brandId} onChange={handleChangeBrand} disabled={brands == null}>
                                    <option value="">{brands == null ? 'Loading brands…' : 'No brand'}</option>
                                    {(brands || []).map(b => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
                                </select>}
                        </div>
                    </section>

                    {/* ---------- Sizes & prices ---------- */}
                    <section className='mv-card'>
                        <h2 className='mv-card-title'>Sizes &amp; prices</h2>
                        {listItems && listItems.length > 0 ?
                            <div className='mv-table-wrap mv-table-flat'>
                                <table className='mv-table'>
                                    <thead>
                                        <tr>
                                            <th scope='col'>Size</th>
                                            <th scope='col' className='mv-cell-right'>MRP</th>
                                            <th scope='col' className='mv-cell-right'>Off</th>
                                            <th scope='col' className='mv-cell-right'>Price</th>
                                            <th scope='col' className='mv-cell-right'>Stock</th>
                                            <th scope='col'><span className='mv-visually-hidden'>Edit</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {listItems.map(s =>
                                            <tr key={s.id} className='mv-row-link' onClick={() => openSizeEditor(s)}>
                                                <td className='mv-cell-strong'>{s.size}</td>
                                                <td className='mv-cell-right'>{formatRupee(s.mrp)}</td>
                                                <td className='mv-cell-right'>{Number(s.discount) > 0 ? `${s.discount}%` : '–'}</td>
                                                <td className='mv-cell-right mv-cell-strong'>{formatRupee(s.finalPrice)}</td>
                                                <td className='mv-cell-right'>
                                                    {Number(s.availableQuantity) > 0 ? s.availableQuantity :
                                                        <span className='mv-stock-out'><HiOutlineExclamation /> 0</span>}
                                                </td>
                                                <td className='mv-cell-right'>
                                                    <button type='button' className='mv-icon-btn mv-icon-btn-sm' aria-label={`Edit size ${s.size}`}
                                                        onClick={(e) => { e.stopPropagation(); openSizeEditor(s) }}>
                                                        <HiOutlinePencil />
                                                    </button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div> :
                            <p className='mv-page-sub'>No sizes yet. Add sizes below so customers can buy this product.</p>}
                        {listItems && listItems.some(s => !(Number(s.availableQuantity) > 0)) &&
                            <p className='mv-field-hint mv-stock-hint'>Sizes with 0 stock are shown as sold out. Tap a size to set its stock.</p>}
                    </section>

                    {/* ---------- Add sizes ---------- */}
                    {sizeList != null &&
                        <form className='mv-card mv-form' onSubmit={updateSizes} noValidate>
                            <h2 className='mv-card-title'>Add sizes</h2>
                            {[['Months', sizeList.months], ['Years', sizeList.years]].map(([label, sizes]) =>
                                sizes && sizes.length > 0 &&
                                <fieldset key={label} className='mv-fieldset'>
                                    <legend className='mv-label'>{label}</legend>
                                    <div className='mv-size-chips'>
                                        {sizes.map(e => {
                                            const added = addedSizes.has(String(e.size))
                                            const checked = selectedIds.includes(e.id)
                                            return (
                                                <label key={e.id} className={`mv-toggle-chip${checked ? ' active' : ''}${added ? ' added' : ''}`}
                                                    title={added ? 'Already added' : undefined}>
                                                    <input type='checkbox' checked={checked} disabled={added} onChange={() => handleCheckboxChange(e.id)} />
                                                    {e.size}
                                                </label>
                                            )
                                        })}
                                    </div>
                                </fieldset>
                            )}

                            <div className='mv-form-grid'>
                                <div className='mv-field'>
                                    <label className='mv-label' htmlFor='init-mrp'>MRP (₹)</label>
                                    <input id='init-mrp' className='mv-input' inputMode='numeric' placeholder='e.g. 499'
                                        value={initialMrp} onChange={e => setInitialMrp(e.target.value.replace(/\D/g, ''))} />
                                </div>
                                <div className='mv-field'>
                                    <label className='mv-label' htmlFor='init-discount'>Discount (%)</label>
                                    <input id='init-discount' className='mv-input' inputMode='numeric' placeholder='0'
                                        value={initialDiscount} onChange={e => setInitialDiscount(e.target.value.replace(/\D/g, ''))} />
                                </div>
                            </div>
                            {addPreview != null && <p className='mv-field-hint'>Customers will pay <strong>{formatRupee(addPreview)}</strong>. New sizes start with 0 stock.</p>}
                            <p className='mv-field-hint'>Need a size that is not listed? Create it in Inventory → Sizes.</p>

                            {addSizesError && <div className='mv-alert mv-alert-error' role='alert'>{addSizesError}</div>}
                            <div className='mv-form-actions'>
                                <button type='submit' className='mv-btn mv-btn-primary' disabled={isAddingSizes}>
                                    {isAddingSizes ? 'Adding…' : selectedIds.length > 0 ? `Add ${selectedIds.length} ${selectedIds.length === 1 ? 'size' : 'sizes'}` : 'Add sizes'}
                                </button>
                            </div>
                        </form>}
                </div>
            </div>

            {/* ---------- Dialogs ---------- */}
            {titleOpen &&
                <Modal title='Product name' onClose={() => setTitleOpen(false)} busy={busy}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => setTitleOpen(false)} disabled={busy}>Cancel</button>
                        <button type='submit' form='title-form' className='mv-btn mv-btn-primary' disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
                    </>}>
                    <form id='title-form' className='mv-form' onSubmit={saveTitle} noValidate>
                        <div className='mv-field'>
                            <label className='mv-label' htmlFor='title-input'>Name shown in the shop</label>
                            <input id='title-input' className='mv-input' value={titleText} onChange={e => setTitleText(e.target.value)} autoFocus />
                        </div>
                        {titleError && <div className='mv-alert mv-alert-error' role='alert'>{titleError}</div>}
                    </form>
                </Modal>}

            {sizeEdit &&
                <Modal title={`Size ${sizeEdit.size}`} onClose={() => setSizeEdit(null)} busy={busy}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => setSizeEdit(null)} disabled={busy}>Cancel</button>
                        <button type='submit' form='size-form' className='mv-btn mv-btn-primary' disabled={busy}>{busy ? 'Saving…' : 'Save size'}</button>
                    </>}>
                    <form id='size-form' className='mv-form mv-size-form' onSubmit={saveSize} noValidate>
                        <div className='mv-size-form-grid'>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='sz-mrp'>MRP (₹)</label>
                                <input id='sz-mrp' className='mv-input' inputMode='numeric' value={sizeEdit.mrp}
                                    onChange={e => setSizeEdit(p => ({ ...p, mrp: e.target.value.replace(/\D/g, '') }))} />
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='sz-discount'>Discount (%)</label>
                                <input id='sz-discount' className='mv-input' inputMode='numeric' value={sizeEdit.discount}
                                    onChange={e => setSizeEdit(p => ({ ...p, discount: e.target.value.replace(/\D/g, '') }))} />
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='sz-qty'>Stock</label>
                                <input id='sz-qty' className='mv-input' inputMode='numeric' value={sizeEdit.quantity}
                                    onChange={e => setSizeEdit(p => ({ ...p, quantity: e.target.value.replace(/\D/g, '') }))} />
                            </div>
                        </div>
                        {livePreview != null &&
                            <div className='mv-size-preview'>Customer pays <strong>{formatRupee(livePreview)}</strong></div>}
                        <div className='mv-field'>
                            <label className='mv-label' htmlFor='sz-desc'>Description</label>
                            <textarea id='sz-desc' className='mv-textarea' rows='3' value={sizeEdit.description} onChange={setSizeField('description')} />
                        </div>
                        <div className='mv-size-form-grid'>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='sz-material'>Material</label>
                                <input id='sz-material' className='mv-input' placeholder='e.g. Cotton' value={sizeEdit.material} onChange={setSizeField('material')} />
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='sz-chest'>Chest (in)</label>
                                <input id='sz-chest' className='mv-input' type='number' min='0' value={sizeEdit.chestSize} onChange={setSizeField('chestSize')} />
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='sz-length'>Length (in)</label>
                                <input id='sz-length' className='mv-input' type='number' min='0' value={sizeEdit.length} onChange={setSizeField('length')} />
                            </div>
                        </div>
                        {sizeEditError && <div className='mv-alert mv-alert-error' role='alert'>{sizeEditError}</div>}
                    </form>
                </Modal>}

            {confirmDeleteImage &&
                <Modal title='Delete this photo?' onClose={() => setConfirmDeleteImage(false)} busy={busy}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => setConfirmDeleteImage(false)} disabled={busy}>Cancel</button>
                        <button type='button' className='mv-btn mv-btn-danger-solid' onClick={deleteImage} disabled={busy}>{busy ? 'Deleting…' : 'Delete'}</button>
                    </>}>
                    <div className='mv-delete-preview'>
                        <img src={currentImage} alt='' />
                        <p className='mv-modal-text'>This photo will be removed from the product. This cannot be undone.</p>
                    </div>
                </Modal>}

            <Toast message={toast} onDone={() => setToast('')} />
        </main>
    )
}

export default EditItem
